import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { CircleX, TriangleAlert } from 'lucide-react';
import { PersonCell, StatusPill, TableCard } from '../../../components/ui';
import type { Employee } from '../../../store/slices/employeeSlice';
import { sbg } from '../../../theme/sbgTokens';
import { formatNumber, MISSING } from '../../../utils/format';
import type { AuditedEmployee, SetupIssue } from '../SetupHealth';
import { displayName, EditEmployeeButton } from './IssueGroups';

/** Issues shown per row before "+N more"; the rest are one click away in the form. */
const ISSUE_PREVIEW = 3;

/**
 * The actions column stays pinned to the right edge, so "Edit" is reachable
 * even when the table is wider than its card and scrolls sideways.
 */
const stickyEnd = { position: 'sticky', right: 0 } as const;
const stickyEndBody = {
  ...stickyEnd,
  zIndex: 1,
  bgcolor: sbg.bg.surface,
  '.MuiTableRow-root:hover > &': { bgcolor: sbg.bg.hover },
} as const;
// Above the body's sticky cells, matching the header's own background.
const stickyEndHead = { ...stickyEnd, zIndex: 3, bgcolor: sbg.bg.surfaceSubtle } as const;

/** One issue: severity icon and word for assistive tech, label, detail on hover. */
const IssueItem: React.FC<{ issue: SetupIssue }> = ({ issue }) => {
  const critical = issue.severity === 'critical';
  const Icon = critical ? CircleX : TriangleAlert;
  return (
    <Box component="li" sx={{ display: 'inline-flex', minWidth: 0 }}>
      <Tooltip title={issue.detail} describeChild>
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'flex-start', gap: 0.5, minWidth: 0 }}>
          <Box
            component="span"
            aria-hidden="true"
            sx={{ display: 'inline-flex', flex: 'none', pt: '3px', color: sbg.status[critical ? 'danger' : 'warning'].icon }}
          >
            <Icon size={12} />
          </Box>
          <Box component="span" sx={{ color: critical ? sbg.text.primary : sbg.text.secondary }}>
            <span className="sbg-sr-only">{critical ? 'Broken: ' : 'Partial: '}</span>
            {issue.label}
          </Box>
        </Box>
      </Tooltip>
    </Box>
  );
};

const IssueList: React.FC<{ employee: Employee; issues: SetupIssue[] }> = ({ employee, issues }) => {
  // The audit already sorts each person's issues critical-first.
  const shown = issues.slice(0, ISSUE_PREVIEW);
  const rest = issues.slice(ISSUE_PREVIEW);
  return (
    <Box
      component="ul"
      aria-label={`Issues for ${displayName(employee)}`}
      sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.5 }}
    >
      {shown.map((issue) => (
        <IssueItem key={issue.key} issue={issue} />
      ))}
      {rest.length > 0 && (
        <Box component="li" sx={{ display: 'inline-flex' }}>
          <Tooltip title={rest.map((issue) => issue.label).join(' · ')} describeChild>
            <Box component="span" sx={{ color: sbg.text.secondary, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
              <span aria-hidden="true">+{formatNumber(rest.length)} more</span>
              <span className="sbg-sr-only">Also: {rest.map((issue) => issue.label).join(', ')}</span>
            </Box>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
};

/**
 * The employee view: one row per flagged employee, broken first. Clicking a
 * row (or its Edit button, which is the keyboard path) opens the employee
 * form, where most issues are fixed in one pass.
 */
const EmployeeTable: React.FC<{ rows: AuditedEmployee[]; onEdit: (employee: Employee) => void }> = ({ rows, onEdit }) => (
  <TableCard aria-label="Employees with setup issues" maxHeight={640}>
    <Table stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell>Employee</TableCell>
          <TableCell>Department and team</TableCell>
          <TableCell>Manager</TableCell>
          <TableCell>Issues</TableCell>
          <TableCell align="right" sx={stickyEndHead}>
            <span className="sbg-sr-only">Actions</span>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map(({ employee, issues, hasCritical }) => (
          <TableRow key={employee._id} hover onClick={() => onEdit(employee)} sx={{ cursor: 'pointer' }}>
            <TableCell sx={{ minWidth: 200, maxWidth: 260 }}>
              <PersonCell
                avatarName={employee.name}
                name={
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Box component="span" sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {displayName(employee)}
                    </Box>
                    <StatusPill tone={hasCritical ? 'danger' : 'warning'} sx={{ flex: 'none' }}>
                      {hasCritical ? 'Broken' : 'Partial'}
                    </StatusPill>
                  </Box>
                }
                secondary={(employee.email || '').trim() || 'No email'}
              />
            </TableCell>
            <TableCell sx={{ minWidth: 140, maxWidth: 200 }}>
              <Box>{employee.department?.name || MISSING}</Box>
              <Typography variant="body2" component="div" sx={{ color: sbg.text.secondary }}>
                {employee.teamName || MISSING}
              </Typography>
            </TableCell>
            <TableCell sx={{ minWidth: 150, maxWidth: 220 }}>
              <Box>{(employee.managerName || '').trim() || MISSING}</Box>
              <Typography variant="body2" component="div" sx={{ color: sbg.text.secondary }}>
                {employee.managerUserId ? 'Account linked' : 'Not linked'}
              </Typography>
            </TableCell>
            <TableCell sx={{ minWidth: 240, maxWidth: 420 }}>
              <IssueList employee={employee} issues={issues} />
            </TableCell>
            <TableCell align="right" sx={stickyEndBody}>
              <EditEmployeeButton employee={employee} onEdit={onEdit} label="Edit" />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableCard>
);

export default EmployeeTable;
